import {
  normalizeClinicianWorkbenchPatientRecord,
  type ClinicianWorkbenchActionInput,
  type ClinicianWorkbenchActionReceipt,
  type ClinicianWorkbenchApprovalSummary,
  type ClinicianWorkbenchClinicalNote,
  type ClinicianWorkbenchDerivedFlagKey,
  type ClinicianWorkbenchDetailSection,
  type ClinicianWorkbenchDeliverySummary,
  type ClinicianWorkbenchDocumentSummary,
  type ClinicianWorkbenchOutboundCallAction,
  type ClinicianWorkbenchOutboundCallsCommandRequestV1,
  type ClinicianWorkbenchOutboundManualAnswerV1,
  type ClinicianWorkbenchMedicationCatalogCandidateV1,
  type ClinicianWorkbenchOutboundQuestionnaireCallV1,
  type ClinicianWorkbenchPatientListItem,
  type ClinicianWorkbenchQuestionnaireDefinitionResponseV1,
  type ClinicianWorkbenchResponseV1,
  type ClinicianWorkbenchSelectedPatient,
  type ClinicianWorkbenchTaskAssignmentSummary,
  type ClinicianWorkbenchVoiceCallSummary,
  type WorkspaceChangedScopeV1,
} from "@amplecare/contracts";
import { translationKeys, type TranslationKey } from "@amplecare/i18n";

import type {
  ApprovalSummaryViewModel,
  ClinicianWorkbenchRequestState,
  ClinicalNoteViewModel,
  DeliverySummaryViewModel,
  DetailRowKind,
  DetailSectionViewModel,
  DetailSectionSummaryMetricViewModel,
  DocumentSummaryViewModel,
  MetricGroupViewModel,
  PatientListTabFixture,
  PatientListSummaryViewModel,
  PatientWorkspaceViewModel,
  TaskAssignmentSummaryViewModel,
  VoiceCallSummaryViewModel,
  WorkbenchPanelId,
  WorkbenchPanelViewModel,
} from "./types.js";
import {
  buildClinicianWorkbenchShellRequest,
  loadClinicianWorkbenchClientResult,
  loadClinicianWorkbenchOutboundCallsClientResult,
  submitClinicianWorkbenchActionClientResult,
  submitClinicianWorkbenchOutboundCallsCommandClientResult,
  type ClinicianWorkbenchTransport,
} from "./workbench-client.js";
import { resolveMobileRuntimeConfig, type MobileRuntimeConfig } from "../config/runtime-config.js";
import { isMatchingOutboundResponsePatient } from "./clinician/editor-safety.js";
import { resolvePatientContextSummaryText } from "./clinician/patient-list-row.js";
import { createFetchClinicianWorkbenchTransport } from "./workbench-fetch-transport.js";
import {
  prepareBrowserDocumentUploadPayload,
  type BrowserDocumentUploadFile,
} from "./document-upload-client.js";

declare const __AMPLECARE_ENABLE_SCAFFOLD_TRANSPORT__: boolean;

export interface ClinicianWorkbenchShellData {
  request: Awaited<ReturnType<typeof loadClinicianWorkbenchClientResult>>["request"];
  response: ClinicianWorkbenchResponseV1;
  metricGroups: MetricGroupViewModel[];
  listSummary: PatientListSummaryViewModel;
  patientWorkspaces: PatientWorkspaceViewModel[];
  selectedPatient: PatientWorkspaceViewModel | null;
  detailSections: DetailSectionViewModel[];
  workbenchPanels: WorkbenchPanelViewModel[];
}

export interface ClinicianWorkbenchShellActionResult {
  receipt: ClinicianWorkbenchActionReceipt;
  shellData: ClinicianWorkbenchShellData;
  changedScopes?: WorkspaceChangedScopeV1[];
}

export interface ClinicianWorkbenchShellDocumentUploadResult {
  changedScopes?: WorkspaceChangedScopeV1[];
  ingestionStatus: string;
  shellData: ClinicianWorkbenchShellData;
}

export interface ClinicianWorkbenchOutboundCallsShellData {
  generatedAt: string;
  items: ClinicianWorkbenchOutboundQuestionnaireCallV1[];
  patientId: string;
}

export interface ClinicianWorkbenchOutboundCallsCommandResult {
  attemptId: string | null;
  contactId: string | null;
  generatedAt: string;
  items: ClinicianWorkbenchOutboundQuestionnaireCallV1[];
  medicationCatalogCandidates?:
    | ClinicianWorkbenchMedicationCatalogCandidateV1[]
    | null;
  patientId: string;
  sessionId: string | null;
  summaryText: string;
  targetId: string | null;
}

export interface ClinicianWorkbenchOutboundQuestionnaireAnswersPdfResult {
  bodyBytes: ArrayBuffer;
  contentType: string;
  filename: string;
}

export const patientListTabs: PatientListTabFixture[] = [
  {
    id: "my",
    labelKey: translationKeys.shell.workspaceListMyPatients,
  },
  {
    id: "all",
    labelKey: translationKeys.shell.workspaceListAllPatients,
  },
];

export const workbenchPanelTabs: Array<{
  id: WorkbenchPanelId;
  labelKey: TranslationKey;
}> = [
  {
    id: "inbox",
    labelKey: translationKeys.navigation.inbox,
  },
  {
    id: "tasks",
    labelKey: translationKeys.navigation.tasks,
  },
  {
    id: "documents",
    labelKey: translationKeys.navigation.documents,
  },
];

const clinicianWorkbenchTransportCache = new Map<
  string,
  ClinicianWorkbenchTransport
>();

const derivedFlagLabelKeyMap: Record<ClinicianWorkbenchDerivedFlagKey, TranslationKey> = {
  "pre-op": translationKeys.shell.flagPreOp,
  "post-op": translationKeys.shell.flagPostOp,
  "imaging-active": translationKeys.shell.flagImagingActive,
  "pre-admission": translationKeys.shell.flagPreAdmission,
  "chronic-follow-up": translationKeys.shell.flagChronicFollowUp,
};

function mapDerivedFlagLabelKeys(
  derivedFlagKeys: ClinicianWorkbenchDerivedFlagKey[],
): TranslationKey[] {
  return derivedFlagKeys.map((flagKey) => derivedFlagLabelKeyMap[flagKey]);
}

function resolveDetailRowKind(rowId: string): DetailRowKind {
  if (rowId.startsWith("task-assignment-")) {
    return "task-assignment";
  }

  if (rowId.startsWith("approval-")) {
    return "approval";
  }

  if (rowId.startsWith("delivery-")) {
    return "delivery";
  }

  if (rowId.startsWith("document-")) {
    return "document";
  }

  return "default";
}

function resolveDetailRowBadgeLabelKey(
  kind: DetailRowKind,
): TranslationKey | null {
  switch (kind) {
    case "task-assignment":
      return translationKeys.shell.workspaceDetailRowTask;
    case "approval":
      return translationKeys.shell.workspaceDetailRowApproval;
    case "delivery":
      return translationKeys.shell.workspaceDetailRowDelivery;
    case "document":
      return translationKeys.shell.workspaceDetailRowDocument;
    default:
      return null;
  }
}

function buildDetailSectionSummaryMetrics(
  sectionId: ClinicianWorkbenchDetailSection["id"],
  rowKinds: DetailRowKind[],
): DetailSectionSummaryMetricViewModel[] {
  if (sectionId === "case-summary") {
    const taskAssignments = rowKinds.filter((kind) => kind === "task-assignment").length;
    const approvals = rowKinds.filter((kind) => kind === "approval").length;
    const deliveries = rowKinds.filter((kind) => kind === "delivery").length;

    return [
      createMetric(
        "task-assignments",
        translationKeys.shell.workspaceDetailTaskAssignments,
        String(taskAssignments),
        "accent",
      ),
      createMetric(
        "approvals",
        translationKeys.shell.workspaceDetailApprovals,
        String(approvals),
        "warning",
      ),
      createMetric(
        "deliveries",
        translationKeys.shell.workspaceDetailDeliveries,
        String(deliveries),
        "info",
      ),
    ].filter((metric) => metric.valueText !== "0");
  }

  if (sectionId === "documents") {
    const documents = rowKinds.filter((kind) => kind === "document").length;

    return documents > 0
      ? [
          {
            id: "documents",
            labelKey: translationKeys.shell.workspaceDetailDocumentsCount,
            valueText: String(documents),
            tone: "neutral",
          },
        ]
      : [];
  }

  return [];
}

function mapClinicalNotes(
  clinicalNotes: ClinicianWorkbenchClinicalNote[] | undefined,
): ClinicalNoteViewModel[] {
  return (clinicalNotes ?? []).map((note) => ({
    id: note.noteId,
    targetKind: note.targetKind,
    targetId: note.targetId,
    status: note.status ?? "active",
    titleText: note.title,
    bodyText: note.bodyText,
    authoredAtText: note.authoredAt
      ? note.authoredAt.slice(0, 16).replace("T", " ")
      : null,
    authoredByText: note.authoredByLabel,
    visibility: note.visibility,
    sourceText: note.sourceLabel,
  }));
}

function mapDetailSections(
  detailSections: ClinicianWorkbenchDetailSection[],
): DetailSectionViewModel[] {
  return detailSections.map((section) => {
    const rows = section.rows.map((row) => {
      const kind = resolveDetailRowKind(row.id);

      return {
        id: row.id,
        kind,
        badgeLabelKey: resolveDetailRowBadgeLabelKey(kind),
        labelText: row.label,
        valueText: row.valueText,
        clinicalStatus: row.clinicalStatus ?? "active",
        notes: mapClinicalNotes(row.notes),
      };
    });

    return {
      id: section.id,
      titleText: section.title,
      summaryText: section.summaryText,
      summaryMetrics: buildDetailSectionSummaryMetrics(
        section.id,
        rows.map((row) => row.kind),
      ),
      rows,
      notes: mapClinicalNotes(section.notes),
    };
  });
}

function createMetric(
  id: string,
  labelKey: TranslationKey,
  valueText: string,
  tone: DetailSectionSummaryMetricViewModel["tone"],
): DetailSectionSummaryMetricViewModel {
  return {
    id,
    labelKey,
    valueText,
    tone,
  };
}

function mapTaskAssignments(
  taskAssignments: ClinicianWorkbenchTaskAssignmentSummary[],
): TaskAssignmentSummaryViewModel[] {
  return taskAssignments.map((assignment) => ({
    id: assignment.assignmentId,
    taskLabel: assignment.taskLabel,
    targetLabel: assignment.targetLabel,
    targetKindText: assignment.targetKind,
    statusText: assignment.statusLabel,
    canComplete: assignment.canComplete,
  }));
}

function mapApprovalSummaries(
  approvalSummaries: ClinicianWorkbenchApprovalSummary[],
): ApprovalSummaryViewModel[] {
  return approvalSummaries.map((approval) => ({
    id: approval.approvalId,
    labelText: approval.label,
    ownerText: approval.ownerLabel,
    statusText: approval.statusLabel,
    updatedAtText: approval.updatedAtText,
  }));
}

function mapDeliverySummaries(
  deliverySummaries: ClinicianWorkbenchDeliverySummary[],
): DeliverySummaryViewModel[] {
  return deliverySummaries.map((delivery) => ({
    id: delivery.deliveryId,
    channelText: delivery.channelLabel,
    recipientText: delivery.recipientLabel,
    statusText: delivery.statusLabel,
    updatedAtText: delivery.updatedAtText,
  }));
}

function mapDocumentSummaries(
  documentSummaries: ClinicianWorkbenchDocumentSummary[],
): DocumentSummaryViewModel[] {
  return documentSummaries.map((document) => ({
    id: document.documentId,
    labelText: document.label,
    summaryText: document.summaryText,
    statusText: document.statusLabel,
    createdAtText: document.createdAt.slice(0, 16).replace("T", " "),
    createdByText: document.createdByLabel,
    availability: document.availability,
    canMarkReviewed: document.canMarkReviewed,
    countsTowardPendingDocumentCount: document.countsTowardPendingDocumentCount,
    clinicalStatus: document.clinicalStatus ?? "active",
    notes: mapClinicalNotes(document.notes),
  }));
}

function formatWorkbenchTimestampText(value: string): string {
  return value.slice(0, 16).replace("T", " ");
}

function formatChatTimestampText(value: string): string {
  const today = new Date().toISOString().slice(0, 10);
  const messageDate = value.slice(0, 10);
  const time = value.slice(11, 16);
  return messageDate === today ? time : `${messageDate} ${time}`;
}

function mapVoiceCallSummaries(
  voiceCallSummaries: ClinicianWorkbenchVoiceCallSummary[],
): VoiceCallSummaryViewModel[] {
  return voiceCallSummaries.map((voiceCall) => ({
    id: voiceCall.callId,
    connectContactId: voiceCall.connectContactId,
    receivedAtText: formatWorkbenchTimestampText(voiceCall.receivedAt),
    previewText: voiceCall.previewText,
    sourceText: voiceCall.sourceLabel,
    turnCount: voiceCall.turnCount,
    hasClinicianHandoff: voiceCall.hasClinicianHandoff,
    questionClassText: voiceCall.questionClass,
    transcriptTurns: voiceCall.transcriptTurns.map((turn) => ({
      id: turn.id,
      speakerKind: turn.speakerKind,
      speakerLabelText: turn.speakerLabel,
      bodyText: turn.bodyText,
      timestampText: formatWorkbenchTimestampText(turn.sentAt),
    })),
  }));
}

function buildInboxPanel(
  patientWorkspaces: PatientWorkspaceViewModel[],
): WorkbenchPanelViewModel {
  const attentionCount = patientWorkspaces.filter((patient) => patient.requiresAttention).length;
  const unreadCount = patientWorkspaces.reduce(
    (sum, patient) => sum + patient.unreadMessageCount,
    0,
  );

  return {
    id: "inbox",
    titleKey: translationKeys.navigation.inbox,
    subtitleKey: translationKeys.shell.workspacePanelInboxSubtitle,
    emptyStateKey: translationKeys.shell.workspacePanelEmptyInbox,
    summaryMetrics: [
      createMetric(
        "patients",
        translationKeys.shell.workspacePanelPatients,
        String(patientWorkspaces.length),
        "neutral",
      ),
      createMetric(
        "attention",
        translationKeys.shell.workspacePanelAttention,
        String(attentionCount),
        "warning",
      ),
      createMetric(
        "unread",
        translationKeys.shell.workspacePanelUnread,
        String(unreadCount),
        "accent",
      ),
    ],
    items: patientWorkspaces.map((patient) => ({
      id: `panel-inbox-${patient.id}`,
      titleText: patient.displayName,
      subtitleText: patient.statusText,
      supportingText: patient.summaryText,
      badgeLabelKey: patient.requiresAttention
        ? translationKeys.shell.workspaceListAttentionOnly
        : null,
      metrics: [
        createMetric(
          `patient-unread-${patient.id}`,
          translationKeys.shell.workspacePanelUnread,
          String(patient.unreadMessageCount),
          "accent",
        ),
        createMetric(
          `patient-documents-${patient.id}`,
          translationKeys.shell.workspacePanelPendingDocuments,
          String(patient.pendingDocumentCount),
          "neutral",
        ),
      ],
      tone: patient.requiresAttention ? "warning" : "neutral",
      targetPatientId: patient.id,
      targetDetailTabId: "information",
      action: patient.canAddToMyPatients
        ? {
            kind: "add-patient-to-my-list",
            patientId: patient.id,
            expectedMaterialChangeToken: patient.materialChangeToken,
          }
        : null,
      actionLabelKey: patient.canAddToMyPatients
        ? translationKeys.shell.workspaceAddToMyPatients
        : null,
    })),
  };
}

function buildTasksPanel(
  selectedPatient: PatientWorkspaceViewModel | null,
): WorkbenchPanelViewModel {
  const taskItems = selectedPatient?.taskAssignments ?? [];
  const approvalItems = selectedPatient?.approvalSummaries ?? [];
  const deliveryItems = selectedPatient?.deliverySummaries ?? [];

  return {
    id: "tasks",
    titleKey: translationKeys.navigation.tasks,
    subtitleKey: translationKeys.shell.workspacePanelTasksSubtitle,
    emptyStateKey: translationKeys.shell.workspacePanelEmptyTasks,
    summaryMetrics: [
      createMetric(
        "task-assignments",
        translationKeys.shell.workspaceDetailTaskAssignments,
        String(taskItems.length),
        "accent",
      ),
      createMetric(
        "approvals",
        translationKeys.shell.workspaceDetailApprovals,
        String(approvalItems.length),
        "warning",
      ),
      createMetric(
        "deliveries",
        translationKeys.shell.workspaceDetailDeliveries,
        String(deliveryItems.length),
        "info",
      ),
    ].filter((metric) => metric.valueText !== "0"),
    items: [
      ...taskItems.map((task) => ({
        id: `panel-task-${task.id}`,
        titleText: task.taskLabel,
        subtitleText: `${task.targetKindText}: ${task.targetLabel}`,
        supportingText: task.statusText,
        badgeLabelKey: translationKeys.shell.workspaceDetailRowTask,
        metrics: [],
        tone: "accent" as const,
        targetPatientId: selectedPatient?.id ?? null,
        targetDetailTabId: "case-summary" as const,
        action:
          selectedPatient && task.canComplete
            ? {
                kind: "complete-task-assignment" as const,
                patientId: selectedPatient.id,
                expectedMaterialChangeToken: selectedPatient.materialChangeToken,
                assignmentId: task.id,
              }
            : null,
        actionLabelKey: task.canComplete
          ? translationKeys.shell.workspaceActionCompleteTask
          : null,
      })),
      ...approvalItems.map((approval) => ({
        id: `panel-approval-${approval.id}`,
        titleText: approval.labelText,
        subtitleText: approval.ownerText,
        supportingText: `${approval.statusText} | ${approval.updatedAtText}`,
        badgeLabelKey: translationKeys.shell.workspaceDetailRowApproval,
        metrics: [],
        tone: "warning" as const,
        targetPatientId: selectedPatient?.id ?? null,
        targetDetailTabId: "case-summary" as const,
        action: null,
        actionLabelKey: null,
      })),
      ...deliveryItems.map((delivery) => ({
        id: `panel-delivery-${delivery.id}`,
        titleText: delivery.channelText,
        subtitleText: delivery.recipientText,
        supportingText: `${delivery.statusText} | ${delivery.updatedAtText}`,
        badgeLabelKey: translationKeys.shell.workspaceDetailRowDelivery,
        metrics: [],
        tone: "info" as const,
        targetPatientId: selectedPatient?.id ?? null,
        targetDetailTabId: "case-summary" as const,
        action: null,
        actionLabelKey: null,
      })),
    ],
  };
}

function buildDocumentsPanel(
  selectedPatient: PatientWorkspaceViewModel | null,
): WorkbenchPanelViewModel {
  const documentItems = selectedPatient?.documentSummaries ?? [];

  return {
    id: "documents",
    titleKey: translationKeys.navigation.documents,
    subtitleKey: translationKeys.shell.workspacePanelDocumentsSubtitle,
    emptyStateKey: translationKeys.shell.workspacePanelEmptyDocuments,
    summaryMetrics: [
      createMetric(
        "documents",
        translationKeys.shell.workspaceDetailDocumentsCount,
        String(documentItems.length),
        "neutral",
      ),
      createMetric(
        "pending-documents",
        translationKeys.shell.workspacePanelPendingDocuments,
        String(selectedPatient?.pendingDocumentCount ?? 0),
        "warning",
      ),
    ].filter((metric) => metric.valueText !== "0"),
    items: documentItems.map((document) => ({
      id: `panel-document-${document.id}`,
      titleText: document.labelText,
      subtitleText: document.statusText,
      supportingText: `${document.summaryText} | ${document.createdByText} | ${document.createdAtText}`,
      badgeLabelKey: translationKeys.shell.workspaceDetailRowDocument,
      metrics: [],
      tone:
        document.availability === "download-ready"
          ? ("info" as const)
          : ("neutral" as const),
      targetPatientId: selectedPatient?.id ?? null,
      targetDetailTabId: "documents" as const,
      action:
        selectedPatient && document.canMarkReviewed
          ? {
              kind: "mark-document-reviewed" as const,
              patientId: selectedPatient.id,
              expectedMaterialChangeToken: selectedPatient.materialChangeToken,
              documentId: document.id,
            }
          : null,
      actionLabelKey: document.canMarkReviewed
        ? translationKeys.shell.workspaceActionMarkDocumentReviewed
        : null,
    })),
  };
}

function buildWorkbenchPanels(
  patientWorkspaces: PatientWorkspaceViewModel[],
  selectedPatient: PatientWorkspaceViewModel | null,
): WorkbenchPanelViewModel[] {
  return [
    buildInboxPanel(patientWorkspaces),
    buildTasksPanel(selectedPatient),
    buildDocumentsPanel(selectedPatient),
  ];
}

function formatPatientListActivityTime(value: string | null | undefined): string {
  const trimmedValue = value?.trim();

  if (!trimmedValue) {
    return "";
  }

  const isoTimeMatch = /T(\d{2}:\d{2})/u.exec(trimmedValue);

  return isoTimeMatch?.[1] ?? "";
}

function toPatientWorkspaceViewModel(
  listItem: ClinicianWorkbenchPatientListItem,
  selectedPatient: ClinicianWorkbenchSelectedPatient | null,
  myPatientIds: string[],
): PatientWorkspaceViewModel {
  const inMyPatients = listItem.inMyPatients || myPatientIds.includes(listItem.patientId);
  const detailSections = mapDetailSections(selectedPatient?.detailSections ?? []);
  const queueSummaryText =
    selectedPatient?.summaryText ?? listItem.summaryText;
  const summaryText =
    resolvePatientContextSummaryText({
      summaryText: queueSummaryText,
    }) ?? "";

  return {
    id: listItem.patientId,
    displayName: listItem.displayName,
    summaryText,
    queueSummaryText,
    listMetaText: listItem.identifier,
    statusText: listItem.statusLabel,
    lastActivityText: formatPatientListActivityTime(listItem.lastActivityAt),
    dateOfService: listItem.dateOfService ?? null,
    pendingRedFlagCount:
      selectedPatient?.pendingRedFlagCount ?? listItem.pendingRedFlagCount ?? 0,
    coordinatorText: selectedPatient?.coordinatorLabel ?? "",
    programText: selectedPatient?.programLabel ?? "",
    nextAppointmentText: selectedPatient?.nextAppointmentText ?? "",
    requiresAttention: listItem.requiresAttention,
    inMyPatients,
    canAddToMyPatients:
      !inMyPatients && (selectedPatient?.canAddToMyPatients ?? true),
    unreadMessageCount: listItem.unreadMessageCount,
    pendingDocumentCount: listItem.pendingDocumentCount,
    openOutboundCallCount: listItem.openOutboundCallCount ?? 0,
    hasVoiceCallHandoff: listItem.hasVoiceCallHandoff ?? false,
    materialChangeToken: selectedPatient?.materialChangeToken ?? listItem.materialChangeToken,
    derivedFlagLabelKeys: mapDerivedFlagLabelKeys(listItem.derivedFlagKeys),
    taskAssignments: mapTaskAssignments(selectedPatient?.taskAssignments ?? []),
    approvalSummaries: mapApprovalSummaries(selectedPatient?.approvalSummaries ?? []),
    deliverySummaries: mapDeliverySummaries(selectedPatient?.deliverySummaries ?? []),
    documentSummaries: mapDocumentSummaries(selectedPatient?.documentSummaries ?? []),
    voiceCallSummaries: mapVoiceCallSummaries(selectedPatient?.voiceCallSummaries ?? []),
    detailSections,
    clinicalNotes: mapClinicalNotes(selectedPatient?.clinicalNotes),
    record: normalizeClinicianWorkbenchPatientRecord(selectedPatient?.record),
    chatMessages: (selectedPatient?.conversation.messages ?? []).map((message) => ({
      id: message.messageId,
      senderText: message.senderDisplayName,
      senderKind: message.senderKind,
      bodyText: message.bodyText,
      timestampText: formatChatTimestampText(message.sentAt),
      deliveryState: message.deliveryState,
      alignment:
        message.senderKind === "clinician"
          ? "right"
          : message.senderKind === "system"
            ? "center"
            : "left",
      tone:
        message.senderKind === "patient"
          ? "accent"
          : message.senderKind === "system"
            ? "info"
            : "neutral",
      review: message.review
        ? {
            state: message.review.state,
            confidencePercent: message.review.confidencePercent,
            criticalityPercent: message.review.criticalityPercent,
            clinicianNotesText: message.review.clinicianNotes,
            canApprove: message.review.canApprove,
            canReject: message.review.canReject,
            canEdit: message.review.canEdit,
          }
        : null,
    })),
  };
}

async function resolveClinicianWorkbenchTransport(
  state: ClinicianWorkbenchRequestState,
  runtimeConfig: MobileRuntimeConfig,
): Promise<ClinicianWorkbenchTransport> {
  if (runtimeConfig.clinicianWorkbenchTransportMode === "http") {
    const transportCacheKey = JSON.stringify({
      transportMode: runtimeConfig.clinicianWorkbenchTransportMode,
      apiBaseUrl: runtimeConfig.apiBaseUrl,
      sessionApiBaseUrl: runtimeConfig.sessionApiBaseUrl,
      requestTenantId: runtimeConfig.requestTenantId,
      requestSiteId: runtimeConfig.requestSiteId,
      requestClinicianUserId: runtimeConfig.requestClinicianUserId,
      tenantSessionBootstrapMode: runtimeConfig.tenantSessionBootstrapMode,
      workforceSessionSubjectKey: runtimeConfig.workforceSessionSubjectKey,
    });
    const cachedTransport =
      clinicianWorkbenchTransportCache.get(transportCacheKey);

    if (cachedTransport) {
      return cachedTransport;
    }

    const nextTransport = createFetchClinicianWorkbenchTransport(runtimeConfig);
    clinicianWorkbenchTransportCache.set(transportCacheKey, nextTransport);

    return nextTransport;
  }

  if (__AMPLECARE_ENABLE_SCAFFOLD_TRANSPORT__) {
    const transportCacheKey = JSON.stringify({
      transportMode: runtimeConfig.clinicianWorkbenchTransportMode,
      requestTenantId: runtimeConfig.requestTenantId,
      requestSiteId: runtimeConfig.requestSiteId,
      requestClinicianUserId: runtimeConfig.requestClinicianUserId,
      myPatientIds: state.myPatientIds,
    });
    const cachedTransport =
      clinicianWorkbenchTransportCache.get(transportCacheKey);

    if (cachedTransport) {
      return cachedTransport;
    }

    const { createScaffoldClinicianWorkbenchTransport } = await import(
      "./workbench-scaffold-transport.js"
    );
    const nextTransport = createScaffoldClinicianWorkbenchTransport({
      myPatientIds: state.myPatientIds,
    });
    clinicianWorkbenchTransportCache.set(transportCacheKey, nextTransport);

    return nextTransport;
  }

  throw new Error("clinician-workbench-scaffold-transport-disabled");
}

export async function loadClinicianWorkbenchShellData(
  state: ClinicianWorkbenchRequestState,
  runtimeConfig: MobileRuntimeConfig = resolveMobileRuntimeConfig(),
  signal?: AbortSignal,
): Promise<ClinicianWorkbenchShellData> {
  const transport = await resolveClinicianWorkbenchTransport(state, runtimeConfig);
  const { request, response } = await loadClinicianWorkbenchClientResult(
    state,
    transport,
    runtimeConfig,
    signal,
  );
  return mapClinicianWorkbenchShellData(request, response, state.myPatientIds);
}

function mapClinicianWorkbenchShellData(
  request: Awaited<ReturnType<typeof loadClinicianWorkbenchClientResult>>["request"],
  response: ClinicianWorkbenchResponseV1,
  myPatientIds: string[],
): ClinicianWorkbenchShellData {
  const patientWorkspaces = response.patientList.map((listItem) =>
    toPatientWorkspaceViewModel(
      listItem,
      response.selectedPatient?.patientId === listItem.patientId
        ? response.selectedPatient
        : null,
      myPatientIds,
    ),
  );
  const selectedPatient =
    patientWorkspaces.find(
      (patient) => patient.id === response.routeState.selectedPatientId,
    ) ??
    patientWorkspaces[0] ??
    null;
  const detailSections = selectedPatient?.detailSections ?? [];
  const workbenchPanels = buildWorkbenchPanels(patientWorkspaces, selectedPatient);

  return {
    request,
    response,
    metricGroups: response.metrics.map((group) => ({
      id: group.id,
      titleText: group.title,
      items: group.items.map((item) => ({
        id: item.id,
        labelText: item.label,
        valueText: item.valueText,
      })),
    })),
    listSummary: response.listSummary,
    patientWorkspaces,
    selectedPatient,
    detailSections,
    workbenchPanels,
  };
}

export function getClinicianWorkbenchShellData(
  state: ClinicianWorkbenchRequestState,
  runtimeConfig?: MobileRuntimeConfig,
  signal?: AbortSignal,
): Promise<ClinicianWorkbenchShellData> {
  return loadClinicianWorkbenchShellData(state, runtimeConfig, signal);
}

export async function submitClinicianWorkbenchShellAction(
  state: ClinicianWorkbenchRequestState,
  action: ClinicianWorkbenchActionInput,
  runtimeConfig: MobileRuntimeConfig = resolveMobileRuntimeConfig(),
  signal?: AbortSignal,
): Promise<ClinicianWorkbenchShellActionResult> {
  const transport = await resolveClinicianWorkbenchTransport(state, runtimeConfig);
  const { request, response } = await submitClinicianWorkbenchActionClientResult(
    state,
    action,
    transport,
    runtimeConfig,
    signal,
  );

  return {
    receipt: response.receipt,
    changedScopes: response.changedScopes,
    shellData: mapClinicianWorkbenchShellData(
      request.workbenchRequest,
      response.workbench,
      state.myPatientIds,
    ),
  };
}

export async function loadClinicianWorkbenchShellOutboundCalls(
  state: ClinicianWorkbenchRequestState,
  patientId: string,
  runtimeConfig: MobileRuntimeConfig = resolveMobileRuntimeConfig(),
  signal?: AbortSignal,
): Promise<ClinicianWorkbenchOutboundCallsShellData> {
  const transport = await resolveClinicianWorkbenchTransport(state, runtimeConfig);
  const result = await loadClinicianWorkbenchOutboundCallsClientResult(
    state,
    patientId,
    transport,
    runtimeConfig,
    signal,
  );

  return {
    generatedAt: result.response.generatedAt,
    items: result.response.items,
    patientId: result.response.patientId,
  };
}

export async function loadClinicianWorkbenchShellQuestionnaireDefinition(
  state: ClinicianWorkbenchRequestState,
  runtimeConfig: MobileRuntimeConfig = resolveMobileRuntimeConfig(),
  signal?: AbortSignal,
  options: { questionnaireTemplateVersionId?: string | null } = {},
): Promise<ClinicianWorkbenchQuestionnaireDefinitionResponseV1> {
  const transport = await resolveClinicianWorkbenchTransport(state, runtimeConfig);
  const workbenchRequest = buildClinicianWorkbenchShellRequest(
    state,
    runtimeConfig,
  );

  return await transport.readClinicianWorkbenchQuestionnaireDefinition({
    request: {
      tenantId: workbenchRequest.tenantId,
      siteId: workbenchRequest.siteId,
      clinicianUserId: workbenchRequest.clinicianUserId,
      ...(options.questionnaireTemplateVersionId
        ? { questionnaireTemplateVersionId: options.questionnaireTemplateVersionId }
        : {}),
    },
    signal,
  });
}

export async function submitClinicianWorkbenchShellOutboundCallsCommand(
  state: ClinicianWorkbenchRequestState,
  input: {
    action: ClinicianWorkbenchOutboundCallAction;
    acknowledgeOutsideWindow?: boolean | null;
    acknowledgeRepeat?: boolean | null;
    escalationReasonKey?: string | null;
    attemptId?: string | null;
    patientId: string;
    reasonText?: string | null;
    reviewNotes?: string | null;
    reviewOwnerKind?: "clinician" | "team" | "department" | "queue" | null;
    reviewOwnerReference?: string | null;
    sessionId?: string | null;
    targetId: string;
    medicationQuery?: string | null;
    medicationQuestionKey?: string | null;
    medicationEntryIndex?: number | null;
    resolvedDrugName?: string | null;
    resolvedStrength?: string | null;
    resolvedRxcui?: string | null;
    manualAnswers?: ClinicianWorkbenchOutboundManualAnswerV1[] | null;
    clearedQuestionKeys?: string[] | null;
    expectedSessionRevision?: string | null;
  },
  runtimeConfig: MobileRuntimeConfig = resolveMobileRuntimeConfig(),
  signal?: AbortSignal,
): Promise<ClinicianWorkbenchOutboundCallsCommandResult> {
  const transport = await resolveClinicianWorkbenchTransport(state, runtimeConfig);
  const commandRequest: Omit<
    ClinicianWorkbenchOutboundCallsCommandRequestV1,
    "clinicianUserId" | "schemaVersion" | "siteId" | "tenantId"
  > = {
    patientId: input.patientId,
    command: {
      action: input.action,
      acknowledgeOutsideWindow: input.acknowledgeOutsideWindow ?? null,
      acknowledgeRepeat: input.acknowledgeRepeat ?? null,
      attemptId: input.attemptId ?? null,
      escalationReasonKey: input.escalationReasonKey ?? null,
      reasonText: input.reasonText ?? null,
      reviewNotes: input.reviewNotes ?? null,
      reviewOwnerKind: input.reviewOwnerKind ?? null,
      reviewOwnerReference: input.reviewOwnerReference ?? null,
      sessionId: input.sessionId ?? null,
      targetId: input.targetId,
      medicationQuery: input.medicationQuery ?? null,
      medicationQuestionKey: input.medicationQuestionKey ?? null,
      medicationEntryIndex: input.medicationEntryIndex ?? null,
      resolvedDrugName: input.resolvedDrugName ?? null,
      resolvedStrength: input.resolvedStrength ?? null,
      resolvedRxcui: input.resolvedRxcui ?? null,
      manualAnswers: input.manualAnswers ?? null,
      clearedQuestionKeys: input.clearedQuestionKeys ?? null,
      expectedSessionRevision: input.expectedSessionRevision ?? null,
    },
  };
  const result = await submitClinicianWorkbenchOutboundCallsCommandClientResult(
    state,
    commandRequest,
    transport,
    runtimeConfig,
    signal,
  );

  if (
    !isMatchingOutboundResponsePatient(
      input.patientId,
      result.response.patientId,
    )
  ) {
    throw new Error("clinician-outbound-calls-patient-mismatch");
  }

  return {
    attemptId: result.response.attemptId,
    contactId: result.response.contactId,
    generatedAt: result.response.generatedAt,
    items: result.response.items,
    medicationCatalogCandidates:
      result.response.medicationCatalogCandidates ?? null,
    patientId: result.response.patientId,
    sessionId: result.response.sessionId,
    summaryText: result.response.summaryText,
    targetId: result.response.targetId,
  };
}

export async function downloadClinicianWorkbenchShellOutboundQuestionnaireAnswersPdf(
  state: ClinicianWorkbenchRequestState,
  input: {
    patientId: string;
    sessionId?: string | null;
    targetId: string;
  },
  runtimeConfig: MobileRuntimeConfig = resolveMobileRuntimeConfig(),
  signal?: AbortSignal,
): Promise<ClinicianWorkbenchOutboundQuestionnaireAnswersPdfResult> {
  const transport = await resolveClinicianWorkbenchTransport(state, runtimeConfig);

  return await transport.downloadClinicianWorkbenchOutboundQuestionnaireAnswersPdf({
    patientId: input.patientId,
    sessionId: input.sessionId ?? null,
    signal,
    targetId: input.targetId,
  });
}

export async function uploadClinicianWorkbenchShellDocument(
  state: ClinicianWorkbenchRequestState,
  input: {
    documentTypeKey?: string | null;
    file: BrowserDocumentUploadFile;
    patientId: string;
    title?: string | null;
  },
  runtimeConfig: MobileRuntimeConfig = resolveMobileRuntimeConfig(),
  signal?: AbortSignal,
): Promise<ClinicianWorkbenchShellDocumentUploadResult> {
  const transport = await resolveClinicianWorkbenchTransport(state, runtimeConfig);
  const workbenchRequest = buildClinicianWorkbenchShellRequest(
    state,
    runtimeConfig,
  );
  const preparedUpload = await prepareBrowserDocumentUploadPayload(input.file);
  const title = input.title?.trim() || preparedUpload.title;
  const response = await transport.uploadClinicianWorkbenchDocument({
    bytes: preparedUpload.bytes,
    request: {
      workbenchRequest,
      patientId: input.patientId,
      filename: preparedUpload.filename,
      mediaType: preparedUpload.mediaType,
      byteLength: preparedUpload.byteLength,
      sha256: preparedUpload.sha256Hex,
      title,
      documentTypeKey: input.documentTypeKey ?? null,
    },
    signal,
  });

  return {
    changedScopes: response.changedScopes,
    ingestionStatus: response.ingestion.ingestionStatus,
    shellData: mapClinicianWorkbenchShellData(
      workbenchRequest,
      response.workbench,
      state.myPatientIds,
    ),
  };
}
